import { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { Avatar } from '../Avatar';
import { MESSAGE_LIMITS } from '@twinden/shared';
import type { ChatConversation } from './ConversationDialogs';
import { conversationName, replyLabel, type ChatMessage } from './messageUtils';
import { memberLabelsAtom } from '../../store';
import { useAtomValue } from 'jotai';
import { useT } from '../../i18n';

interface ForwardDialogProps {
  message: ChatMessage | null;
  /** The family's chats (D29: forwarding stays inside the family). */
  conversations: ChatConversation[];
  currentUserId: string | undefined;
  onClose: () => void;
  onForward: (message: ChatMessage, conversationIds: string[]) => Promise<void>;
}

// Pick up to FORWARD_MAX_TARGETS chats to forward a message to (M22, D29).
export function ForwardDialog({
  message,
  conversations,
  currentUserId,
  onClose,
  onForward,
}: ForwardDialogProps) {
  const memberLabels = useAtomValue(memberLabelsAtom);
  const t = useT();
  const [selected, setSelected] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const size = useBreakpointValue({ base: 'full', md: 'md' }) ?? 'md';
  const max = MESSAGE_LIMITS.FORWARD_MAX_TARGETS;

  useEffect(() => setSelected([]), [message?.id]);

  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : current.length < max
          ? [...current, id]
          : current
    );

  const submit = async () => {
    if (!message || selected.length === 0) return;
    setSending(true);
    try {
      await onForward(message, selected);
      onClose();
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      isOpen={message !== null}
      onClose={onClose}
      size={size}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('chat.forward.title')}
          {message && (
            <Text
              fontSize="sm"
              fontWeight="normal"
              color="gray.500"
              noOfLines={1}
            >
              {replyLabel(message)}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={3}>
            {conversations.map((conversation) => {
              const name = conversationName(
                conversation,
                currentUserId,
                memberLabels
              );
              const checked = selected.includes(conversation.id);
              return (
                <Checkbox
                  key={conversation.id}
                  isChecked={checked}
                  isDisabled={!checked && selected.length >= max}
                  onChange={() => toggle(conversation.id)}
                >
                  <HStack spacing={3}>
                    <Avatar
                      size="sm"
                      name={name}
                      src={conversation.avatarUrl ?? undefined}
                      ownerId={conversation.id}
                      familyId={conversation.familyId}
                    />
                    <Text noOfLines={1}>{name}</Text>
                  </HStack>
                </Checkbox>
              );
            })}
          </Stack>
        </ModalBody>
        <ModalFooter pb="max(env(safe-area-inset-bottom), 1rem)">
          <Text fontSize="sm" color="gray.500" mr="auto">
            {t('chat.forward.selected', { n: selected.length, max })}
          </Text>
          <Button variant="ghost" mr={2} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => void submit()}
            isDisabled={selected.length === 0}
            isLoading={sending}
          >
            {t('chat.forward.title')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
